// Funding and Relationship: two readings of the cached Apollo payload (GRO-159).
//
// The payload is joined here rather than spliced into the book, the same way the ranking is:
// the nightly ladder writes `company_funding` per account and `/api/book/funding` returns all
// of it. Everything in this file is a pure function over a row, so the table cell, the sort,
// the filters and the CSV all read one definition.
//
// One rule runs through all of it: **an absent value is never a "no".** "None of our funds"
// needs an investor list to have been read. No row, no company at the domain, a different
// company, a public company with no rounds: all of those read "Couldn't find out".

import type {
  BookAccount, CompanyFunding, FundTie, FundingPayload, FundingStage,
} from '../api/types';

export type FundedFields = { funding?: CompanyFunding | null };

export const UNKNOWN_TEXT = 'Couldn’t find out';
export const NONE_TEXT = 'None of our funds';
export const RECENT_ROUND_DAYS = 90;

// Board sort order, earliest first. `unknown` has no rank, so sort.ts pushes it last.
export const STAGE_ORDER: FundingStage[] = [
  'pre_seed', 'seed', 'series_a', 'series_b', 'series_c', 'series_d_plus',
  'late_stage', 'venture', 'debt', 'other', 'acquired', 'public',
];

export const STAGE_LABELS: Record<FundingStage, string> = {
  pre_seed: 'Pre-seed / angel', seed: 'Seed', series_a: 'Series A', series_b: 'Series B',
  series_c: 'Series C', series_d_plus: 'Series D+', late_stage: 'Growth',
  venture: 'Venture', debt: 'Debt', other: 'Other', acquired: 'Acquired',
  public: 'Public', unknown: UNKNOWN_TEXT,
};

// Apollo's own words for these four read badly in a narrow cell ("Venture (Round not
// Specified)", "Debt Financing"), so the cell names the stage instead. A named Seed or Series
// round keeps Apollo's label, which is what a strategist would say.
const STAGES_NAMED_BY_US: FundingStage[] = ['late_stage', 'venture', 'debt', 'other'];

function roundLabel(round: { stage: FundingStage; label: string }): string {
  if (STAGES_NAMED_BY_US.includes(round.stage)) return STAGE_LABELS[round.stage];
  return round.label;
}

// Chip text. The CSV and the tooltip keep the full name.
const FUND_SHORT_NAMES: Record<string, string> = {
  'Kleiner Perkins': 'Kleiner',
  'Index Ventures': 'Index',
  'Lightspeed Venture Partners': 'Lightspeed',
  'NVIDIA / NVentures': 'NVIDIA',
  'General Catalyst': 'Gen. Catalyst',
  'Samsung Ventures / Samsung Next': 'Samsung',
  'A* Capital': 'A*',
};

// The chips a cell shows before collapsing the rest into "+N".
export const VISIBLE_TIES = 2;

// --- The join ----------------------------------------------------------------------------------

export function attachFunding<Row extends BookAccount>(
  rows: Row[],
  payload: FundingPayload | null | undefined,
): (Row & FundedFields)[] {
  const byAccountId = payload?.by_account_id ?? {};
  return rows.map(row => ({ ...row, funding: byAccountId[row.id] ?? null }));
}

export function fundingOf(row: BookAccount): CompanyFunding | null {
  return (row as BookAccount & FundedFields).funding ?? null;
}

// Found means Apollo answered with our company. Anything else carries no facts to show.
function foundFunding(row: BookAccount): CompanyFunding | null {
  const funding = fundingOf(row);
  return funding && funding.status === 'found' ? funding : null;
}

// --- Formatting --------------------------------------------------------------------------------

export function formatUsd(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return '';
  const scaled = (value: number, suffix: string) => {
    const digits = value < 10 && value % 1 !== 0 ? 1 : 0;
    return '$' + value.toFixed(digits).replace(/\.0$/, '') + suffix;
  };
  if (amount >= 1e9) return scaled(amount / 1e9, 'B');
  if (amount >= 1e6) return scaled(amount / 1e6, 'M');
  if (amount >= 1e3) return scaled(amount / 1e3, 'K');
  return '$' + Math.round(amount);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatMonth(month: string | null | undefined): string {
  const found = /^(\d{4})-(\d{2})/.exec(month ?? '');
  if (!found) return '';
  const index = Number(found[2]) - 1;
  return index >= 0 && index < 12 ? `${MONTHS[index]} ${found[1]}` : '';
}

// Apollo dates rounds to the month, so a round is dated the first day of its month and is
// recent while that day is within 90 days of the book's day. A July round reads as recent
// through 29 September, never later: counting whole months would stretch it to ~120 days.
export function roundIsRecent(row: BookAccount, today: string): boolean {
  const round = foundFunding(row)?.latest_round;
  const month = /^(\d{4})-(\d{2})/.exec(round?.date ?? '');
  if (!month || !today) return false;
  const cutoff = new Date(today + 'T00:00:00Z');
  if (Number.isNaN(cutoff.getTime())) return false;
  cutoff.setUTCDate(cutoff.getUTCDate() - RECENT_ROUND_DAYS);
  const roundDay = Date.UTC(Number(month[1]), Number(month[2]) - 1, 1);
  return roundDay >= cutoff.getTime();
}

// --- Funding -----------------------------------------------------------------------------------

export interface FundingReading {
  kind: 'round' | 'public' | 'acquired' | 'unknown';
  // The first line: "Series B", "Growth", "Public", "Acquired", or "Couldn't find out".
  label: string;
  // The second line: "$43M · Oct 2024", the ticker, or who bought them and when.
  detail: string;
  // The tooltip: where the value came from, or why there is none.
  hint: string;
}

function unknownHint(funding: CompanyFunding | null): string {
  if (!funding) return 'Not checked yet. The nightly funding pass reaches every book account within a few nights.';
  if (funding.status === 'no_data') return 'Apollo knows no company at this domain.';
  if (funding.status === 'domain_mismatch') {
    const other = [funding.apollo_name, funding.matched_domain].filter(Boolean).join(' at ');
    return `Apollo answered with a different company${other ? ` (${other})` : ''}, so nothing was kept.`;
  }
  return 'Apollo has no funding round on record for this company.';
}

function checkedHint(funding: CompanyFunding): string {
  const checked = funding.fetched_at ? ` · checked ${funding.fetched_at.slice(0, 10)}` : '';
  const via = funding.matched_by === 'name' ? ` · matched by name at ${funding.matched_domain}` : '';
  return `Apollo${via}${checked}`;
}

export function readFunding(row: BookAccount): FundingReading {
  const funding = fundingOf(row);
  const found = foundFunding(row);
  if (!found) return { kind: 'unknown', label: UNKNOWN_TEXT, detail: '', hint: unknownHint(funding) };

  // Public and acquired outrank the last round: they change what an intro is worth.
  if (found.public?.symbol) {
    return { kind: 'public', label: 'Public', detail: found.public.symbol, hint: checkedHint(found) };
  }
  // The date leads the second line so only a long acquirer name can trim, and it has a tooltip.
  if (found.acquired) {
    const detail = [formatMonth(found.acquired.date), found.acquired.by].filter(Boolean).join(' · ');
    return { kind: 'acquired', label: 'Acquired', detail, hint: checkedHint(found) };
  }
  if (found.latest_round) {
    const round = found.latest_round;
    const detail = [formatUsd(round.amount_usd), formatMonth(round.date)].filter(Boolean).join(' · ');
    return { kind: 'round', label: roundLabel(round), detail, hint: checkedHint(found) };
  }
  return { kind: 'unknown', label: UNKNOWN_TEXT, detail: '', hint: unknownHint(found) };
}

export function fundingStage(row: BookAccount): FundingStage {
  return foundFunding(row)?.stage ?? 'unknown';
}

export function fundingSortValue(row: BookAccount): number | null {
  const index = STAGE_ORDER.indexOf(fundingStage(row));
  return index === -1 ? null : index;
}

export function fundingCsvValue(row: BookAccount): string {
  const reading = readFunding(row);
  if (reading.kind === 'unknown') return '';
  return reading.detail ? `${reading.label} · ${reading.detail}` : reading.label;
}

// --- Relationship ------------------------------------------------------------------------------

export interface RelationshipReading {
  kind: 'funds' | 'none' | 'unknown';
  // Counted ties, strongest first: levels 2 and 3.
  ties: FundTie[];
  // Related vehicles only (Lightspeed India): tooltip, never a chip.
  adjacent: FundTie[];
  hint: string;
}

// Explicit on purpose: "None of our funds" is said only on `no_mutual_found`, which the backend
// sets when it read an investor list. `matched` needs a counted tie to show. Every other state
// -- no row, no investor data, a matched status with nothing countable, a status this code has
// not seen -- is "Couldn't find out", never a "no".
export function readRelationship(row: BookAccount): RelationshipReading {
  const funding = fundingOf(row);
  const found = foundFunding(row);
  const status = found?.relationship.status;
  const ties = found ? found.relationship.funds.filter(tie => tie.strength < 4) : [];
  const adjacent = found ? found.relationship.funds.filter(tie => tie.strength >= 4) : [];
  if (found && status === 'matched' && ties.length) {
    return { kind: 'funds', ties, adjacent, hint: checkedHint(found) };
  }
  if (found && status === 'no_mutual_found') {
    const related = adjacent.length ? ` Related: ${adjacent.map(tie => tie.raw_names.join(', ')).join('; ')}.` : '';
    return { kind: 'none', ties: [], adjacent,
             hint: `Apollo’s investor list names none of our ten funds.${related}` };
  }
  return { kind: 'unknown', ties: [], adjacent: [],
           hint: found ? 'Apollo lists no investors for this company.' : unknownHint(funding) };
}

export function fundShortName(fund: string): string {
  return FUND_SHORT_NAMES[fund] ?? fund;
}

export function strengthLabel(strength: number): string {
  if (strength <= 2) return 'recent round';
  if (strength === 3) return 'small, strategic or older stake';
  return 'related vehicle';
}

// "Kleiner Perkins — recent round · Series B Oct 2024, Series C Nov 2025 · Apollo named it
// Kleiner Perkins". Everything a strategist needs before asking for an intro, in one line.
export function tieTooltip(tie: FundTie): string {
  const rounds = tie.rounds
    .map(entry => [entry.type, formatMonth(entry.date)].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(', ');
  const names = tie.raw_names.length ? ` · Apollo named it ${tie.raw_names.join(', ')}` : '';
  return `${tie.fund} — ${strengthLabel(tie.strength)}${rounds ? ' · ' + rounds : ''}${names}`;
}

// Stronger ties sort first, then more of them. None of ours is 0; unknown sorts last.
export function relationshipSortValue(row: BookAccount): number | null {
  const reading = readRelationship(row);
  if (reading.kind === 'unknown') return null;
  let score = 0;
  for (const tie of reading.ties) score += tie.strength <= 2 ? 100 : 1;
  return score;
}

export function relationshipCsvValue(row: BookAccount): string {
  const reading = readRelationship(row);
  if (reading.kind === 'unknown') return '';
  if (reading.kind === 'none') return NONE_TEXT;
  return reading.ties.map(tie => tie.fund).join('; ');
}

export function hasTieToFund(row: BookAccount, fund: string): boolean {
  return readRelationship(row).ties.some(tie => tie.fund === fund);
}

// Every fund with a counted tie on the rows in scope, alphabetical.
export function fundsInScope(rows: BookAccount[]): string[] {
  const funds = new Set<string>();
  rows.forEach(row => readRelationship(row).ties.forEach(tie => funds.add(tie.fund)));
  return [...funds].sort();
}
